import { useCallback } from "react";

import { createError, parseGraphQLError } from "utils";

import useHub from "data/hub/useHub";

import { useAsyncCall, useRequest } from "hooks";
import { createResolverFactory } from "components/QueryClient/ResolverFactory";

import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";
import {
  useSubscribeToChangePlan,
  useSubscribeToSubscriptionChanged,
} from "scenes/Settings/components/AccountSettings/components/Subscription/hooks";

export function usePlanOption() {
  const selectedHub = useHub();
  const [call] = useAsyncCall();

  const queryFactory = useCallback(() => {
    const query = `
            query planOption {
              practice {
                subscriptions {
                  edges {
                    node {
                      planOption {
                        planId
                      }
                    }
                  }
                }
                pendingChanges
              }
            }`;

    const resolver = ({ data: { practice } }) => {
      const subscription =
        practice?.subscriptions?.edges?.length > 0
          ? practice.subscriptions.edges[0].node
          : undefined;

      if (!subscription || practice?.pendingChanges) return [];

      return subscription.planOption.planId;
    };

    return { query: query, resolver: createResolverFactory(resolver) };
  }, []);

  const { req, clearCache } = useRequest(queryFactory, true);

  useSubscribeToChangePlan({
    onPlanChanged: useCallback(clearCache, [clearCache]),
  });

  useSubscribeToSubscriptionChanged({
    onSubscriptionChanged: useCallback(clearCache, [clearCache]),
  });

  return useCallback(() => {
    return call(async () => {
      try {
        const resp = await req({
          practiceId: toGlobalId("Practice", selectedHub.practiceId),
        });

        return resp;
      } catch (e) {
        const err = createError({
          ...parseGraphQLError(e),
          name: "getPlanOption",
          defaultMessage:
            "Unexpected error occurred when trying to get plan option",
        });
        console.error(err);

        return undefined;
      }
    });
  }, [selectedHub, req, call]);
}

export default usePlanOption;
